import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import type { DataTableFilterField } from '@r/types/index.ts';

export interface StepCollection {
  id?: number;
  company?: { name?: string | null; uniqueCode?: string | null } | null;
  logicTypeStep?: {
    name?: string | null;
    description?: string | null;
    stepPoint?: number | null;
  } | null;
  createdAt?: string | null;
}

export const useStepsFilterFields = (): DataTableFilterField<StepCollection>[] => {
  const { t } = useTranslation();
  const user = useUser().data;
  const { workspace } = UseWorkspace(user);

  // Paliers definis dans la logique du workspace (deja charges, aucun appel supplementaire).
  const stepOptions = (workspace?.currentLogicWorkspace?.steps ?? [])
    .filter((step): step is typeof step & { name: string } => Boolean(step?.name))
    .map((step) => ({ label: step.name, value: step.name }));

  return [
    {
      label: t('label.company.name'),
      value: 'companyName',
      placeholder: t('label.company.name')
    },
    {
      label: t('label.company.unique.code'),
      value: 'companyUniqueCode',
      placeholder: t('label.company.unique.code')
    },
    {
      label: t('label.step.name'),
      value: 'stepName',
      placeholder: t('label.step.name'),
      options: stepOptions
    }
  ];
};

export const useStepsColumns = (): ColumnDef<StepCollection>[] => {
  const { t } = useTranslation();

  return [
    {
      id: 'companyName',
      accessorKey: 'company.name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.company.name')} />
      ),
      enableHiding: false,
      cell: ({ row }) => row.original.company?.name ?? ''
    },
    {
      id: 'companyUniqueCode',
      accessorKey: 'company.uniqueCode',
      header: t('label.company.unique.code'),
      enableSorting: false,
      cell: ({ row }) => row.original.company?.uniqueCode ?? ''
    },
    {
      id: 'stepName',
      accessorKey: 'logicTypeStep.name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.step.name')} />
      ),
      cell: ({ row }) => row.original.logicTypeStep?.name ?? ''
    },
    {
      id: 'stepDescription',
      accessorKey: 'logicTypeStep.description',
      header: t('label.step.description'),
      enableSorting: false,
      cell: ({ row }) => (
        <span
          className="block max-w-[200px] truncate"
          title={row.original.logicTypeStep?.description ?? undefined}
        >
          {row.original.logicTypeStep?.description}
        </span>
      )
    },
    {
      id: 'stepPoint',
      accessorKey: 'logicTypeStep.stepPoint',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.step.point')} />
      ),
      cell: ({ row }) => (
        <FormattedNumber value={row.original.logicTypeStep?.stepPoint ?? 0} style="decimal" />
      )
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.step.reached.at')} />
      ),
      cell: ({ row }) =>
        row.original.createdAt
          ? format(new Date(row.original.createdAt), 'dd/MM/yyyy', { locale: fr })
          : ''
    }
  ];
};
